"use client";

import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { JobPriorityEnumValue } from "@panda0/panda0-graphql/defs";
import {
  Button,
  LoadingSpinner,
  Modal,
  PandaLoader,
  TooltipContent,
  useKeyboardShortcut,
  useRouterStuff,
} from "@panda0/panda0-ui";
import { APP_DOMAIN, getNextPlan } from "@panda0/panda0-utils";
import { AnimatePresence, motion } from "framer-motion";
import { Briefcase, ChevronRight, X } from "lucide-react";
import posthog from "posthog-js";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";

import { mutatePrefix } from "@/lib/swr/mutate";
import useWorkspace from "@/lib/swr/use-workspace";
import { ExpandedJobProps, JobFormData } from "@/lib/types";
import { JobSchema } from "@/lib/zod/schemas/job";
import { FolderDropdown } from "@/ui/folders/folder-dropdown";
import FormDebugPanel from "@/ui/shared/form-debug-panel";
import { UpgradeRequiredToast } from "@/ui/shared/upgrade-required-toast";
import { DraftControls, DraftControlsHandle } from "./draft-controls";
import JobStepsNavigationContent from "./job-steps-navigation";
import { JobBuilderProps, JobModalContext } from "./types";
import { useJobAnalytics } from "./use-job-analytics";
import { JobStep, useJobStateMachine } from "./use-job-state-machine";

export function JobBuilder(props: JobBuilderProps) {
  return props.showJobBuilder ? <JobBuilderOuter {...props} /> : null;
}

function JobBuilderOuter(props: JobBuilderProps) {
  return <JobBuilderInner {...props} />;
}

function JobBuilderInner({
  showJobBuilder,
  setShowJobBuilder,
  props,
  duplicateProps,
}: JobBuilderProps) {
  const searchParams = useSearchParams();
  const { queryParams } = useRouterStuff();
  const {
    id: workspaceId,
    plan,
    nextPlan,
    logo,
    flags,
    conversionEnabled,
  } = useWorkspace();

  const [savedJobId, setSavedJobId] = useState<string>(props?.id || "");
  const [loadingJobData, setLoadingJobData] = useState<boolean>(
    props?.id ? true : false
  );
  const [jobDataLoaded, setJobDataLoaded] = useState<boolean>(
    props?.id ? false : true
  );
  const [jobData, setJobData] = useState<Partial<JobFormData> | null>(null);

  var date = new Date();
  var lastday =
    date.getDate() > 21
      ? new Date(date.getFullYear(), date.getMonth() + 2, 0)
      : new Date(date.getFullYear(), date.getMonth() + 1, 0);

  const defaultValues = props?.id
    ? {}
    : {
      basic: {
        title: "",
        description: "",
        departmentId: "",
        positions: 1,
        experienceStart: 0,
        experienceEnd: 5,
        campaignStart: date,
        campaignEnd: lastday,
        priority: JobPriorityEnumValue.medium,
        tags: [],
        trackConversion: true,
        externalId: "",
        links: [],
      },
      skills: {
        technicalSkills: [],
        cognitiveSkills: [],
      },
      pandaQuiz: {
        questions: [],
      },
      screeningQuestions: [],
      proctoringSettings: {
        PROCTORING_ENABLED: false,
        PROCTORING_MULTIFACE_DETECTION: false,
        PROCTORING_VOICE_MONITORING: false,
        PROCTORING_EYE_TRACKING: false,
        PROCTORING_LIVENESS_ENABLED: false,
        PROCTORING_PERIODIC_SNAPSHOTS: false,
        PROCTORING_RED_FLAG_SNAPSHOTS: false,
      },
      jobDetails: {},
      submissionSettings: {},
      formDetails: {},
    };

  const form = useForm<JobFormData>({
    mode: "onChange",
    reValidateMode: "onChange",
    resolver: zodResolver(JobSchema),
    defaultValues,
  });

  const {
    setValue,
    setError,
    getValues,
    reset,
    formState: { isSubmitting, isDirty },
  } = form;

  const draftControlsRef = useRef<DraftControlsHandle>(null);

  const endpoint = useMemo(
    () =>
      props?.id || savedJobId
        ? {
          method: "PATCH",
          url: `/api/v1/jobs/${props?.id || savedJobId}?workspaceId=${workspaceId}`,
        }
        : {
          method: "POST",
          url: `/api/v1/jobs?workspaceId=${workspaceId}`,
        },
    [props?.id, savedJobId, workspaceId]
  );

  const loadJobData = async () => {
    if (!savedJobId && !props?.id) {
      setJobDataLoaded(true); // For new jobs, mark as loaded immediately
      return;
    }

    setLoadingJobData(true);
    setJobDataLoaded(false);

    try {
      const res = await fetch(
        `/api/v1/jobs/${props?.id || savedJobId}?workspaceId=${workspaceId}`
      );

      if (!res.ok) {
        throw new Error("Failed to load job data");
      }

      const data = await res.json();
      console.log("Job data", data);
      reset({
        ...defaultValues, // Start with defaults
        ...data, // Override with actual data
      });

      setJobData(data);
      setJobDataLoaded(true);
    } catch (error) {
      console.error("Error loading job data:", error);
      toast.error("Failed to load job data");
      setJobDataLoaded(true); // Mark as loaded even on error, to show error UI
    } finally {
      setLoadingJobData(false);
    }
  };

  const saveJobData = useCallback(
    async (data: Partial<JobFormData>) => {
      if (isSubmitting) {
        toast.error("Please save the basic job details first");
        return;
      }

      try {
        const res = await fetch(endpoint.url, {
          method: endpoint.method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        const clonedRes = res.clone();
        if (!res.ok) {
          const { error } = await clonedRes.json();
          if (error?.message?.includes("Upgrade to")) {
            toast.custom(() => (
              <UpgradeRequiredToast
                title={`You've discovered a ${nextPlan.name} feature!`}
                message={error.message}
              />
            ));
            return;
          } else {
            throw new Error("An error occurred");
          }
        }
        const savedData = await res.json();

        // Update jobData with newly saved data
        setJobData((prevData) => ({
          ...prevData,
          ...data,
          ...(savedData.id && { id: savedData.id }),
          ...(savedData &&
            savedData.success &&
            savedData.job && {
            basic: savedData.job.basic,
            formDetails: savedData.job.formDetails,
            skills: savedData.job.skills,
            pandaQuiz: savedData.job.pandaQuiz,
            screeningQuestions: savedData.job.screeningQuestions,
            proctoringSettings: savedData.job.proctoringSettings,
            submissionSettings: savedData.job.submissionSettings,
            videoResume: savedData.job.videoResume,
            quizSettings: savedData.job.quizSettings,
            jobDetails: savedData.job.jobDetails,
            department: savedData.job.department,
            tags: savedData.job.tags?.map((jt) => jt.tag),
          }),
        }));

        if (savedData.id && !savedJobId) {
          setSavedJobId(savedData.id);
        }

        return savedData;
      } catch (error) {
        console.error("Error saving job data:", error);
        throw error;
      }
    },
    [endpoint.url, endpoint.method, isSubmitting, nextPlan.name, savedJobId]
  );

  // Initialize the job state machine
  const stateMachine = useJobStateMachine({
    initialStep: "basic",
    workspaceId,
    savedJobId,
    saveJobData,
    formMethods: form, // Pass the entire form object
    onSaveSuccess: (step, data) => {

    },
    onSaveError: (step, error) => {
      console.error(`Error saving ${step}:`, error);
    },
  });

  const contextValue = useMemo(
    () => ({
      workspaceId,
      workspacePlan: plan,
      workspaceNextPlan: getNextPlan(plan),
      workspaceLogo: logo ?? undefined,
      generatingMetatags: false,
      currentStep: stateMachine.currentStep as any,
      setCurrentStep: (step: JobStep) => stateMachine.navigateToStep(step),
      savedJobId: props?.id || savedJobId,
      saveJobData, // Include the memoized function
      loadingJobData,
      jobData,
      stateMachine, // Pass the state machine to context
    }),
    [
      workspaceId,
      plan,
      logo,
      stateMachine,
      props?.id,
      savedJobId,
      loadingJobData,
      jobData,
      saveJobData, // Include in dependency array
    ]
  );

  // Load job data when savedJobId changes
  useEffect(() => {
    if (savedJobId || props?.id) {
      loadJobData();
    } else {
      reset(defaultValues, { keepDirty: false });
      setJobDataLoaded(true);
    }
  }, [savedJobId, props?.id, reset]);

  const { trackStepView } = useJobAnalytics({
    workspaceId,
    jobId: savedJobId,
  });

  // Track step changes
  useEffect(() => {
    if (stateMachine.currentStep) {
      trackStepView(stateMachine.currentStep as any);
    }
  }, [stateMachine.currentStep, trackStepView]);

  // Track time spent in job builder
  useEffect(() => {
    if (showJobBuilder) {
      // Store start time for analytics
      (window as any).jobBuilderStartTime = Date.now();

      // Track open event
      posthog.capture("job_builder_opened", {
        job_id: props?.id,
        is_new: !props?.id,
        from_duplicate: Boolean(duplicateProps),
        workspace_id: workspaceId,
      });
    }

    return () => {
      // Track close event if job builder was open
      if ((window as any).jobBuilderStartTime) {
        const timeSpentMs = Date.now() - (window as any).jobBuilderStartTime;

        posthog.capture("job_builder_closed", {
          job_id: savedJobId || props?.id,
          time_spent_sec: Math.round(timeSpentMs / 1000),
          was_saved: Boolean(savedJobId),
          workspace_id: workspaceId,
        });

        delete (window as any).jobBuilderStartTime;
      }
    };
  }, [showJobBuilder, props?.id, duplicateProps, workspaceId, savedJobId]);

  return (
    <JobModalContext.Provider value={contextValue}>
      <FormProvider {...form}>
        <Modal
          fullScreen={true}
          preventDefaultClose={true}
          preventEscapeClose={true}
          showModal={showJobBuilder}
          setShowModal={setShowJobBuilder}
          className="max-w-screen-2xl max-h-screen-2xl max-h-full modal-form"
          onClose={() => {
            posthog.capture("job_builder_modal_closed", {
              job_id: savedJobId,
              is_dirty: isDirty,
              workspace_id: workspaceId,
            });

            if (searchParams.has("newJob"))
              queryParams({
                del: ["newJob", "newJobDomain"],
              });
            draftControlsRef.current?.onClose();
            mutatePrefix("/api/v1/jobs");
          }}
        >
          <div className="flex h-full flex-col">
            {/* Header with job info and draft controls */}
            <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
              <div className="flex items-center gap-2">
                {flags?.jobFolders && (
                  <div className="flex items-center gap-2">
                    <FolderDropdown
                      hideViewAll={true}
                      disableAutoRedirect={true}
                      onFolderSelect={(folder) => {
                        setValue("folderId", folder.id, {
                          shouldDirty: true,
                        });
                      }}
                      buttonClassName="max-w-60 md:max-w-[24rem]"
                      buttonTextClassName="text-lg md:text-lg font-medium"
                      {...(props?.folderId && {
                        selectedFolderId: props.folderId,
                      })}
                    />
                    <ChevronRight className="size-4 text-neutral-500" />
                  </div>
                )}

                <Briefcase className="size-6 text-neutral-700" />
                <h3 className="text-lg font-medium">
                  {props
                    ? `${jobData?.basic?.title || "Loading job..."}`
                    : savedJobId
                      ? "Job Setup"
                      : "New Job"}
                </h3>
              </div>

              <div className="flex items-center gap-4">
                {workspaceId && (
                  <DraftControls
                    ref={draftControlsRef}
                    props={props}
                    workspaceId={workspaceId}
                  />
                )}

                <FormDebugPanel />
                <button
                  type="button"
                  onClick={() => {
                    setShowJobBuilder(false);
                    draftControlsRef.current?.onClose();
                    mutatePrefix("/api/v1/jobs");
                  }}
                  className="group rounded-full p-2 text-neutral-500 transition-all duration-75 hover:bg-neutral-100 focus:outline-none active:bg-neutral-200"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Loading state or content */}
            {!jobDataLoaded ? (
              <div className="flex flex-1 items-center justify-center">
                <div className="text-center">
                  <p className="text-lg text-neutral-600">
                    Loading job data...
                  </p>
                </div>
              </div>
            ) : (
              <JobStepsNavigationContent />
            )}
          </div>
        </Modal>
      </FormProvider>
    </JobModalContext.Provider>
  );
}

export function CreateJobButton({
  setShowJobBuilder,
}: {
  setShowJobBuilder: Dispatch<SetStateAction<boolean>>;
}) {
  const { slug, nextPlan, exceededJobs } = useWorkspace();
  useKeyboardShortcut("j", () => {
    posthog.capture("job_builder_keyboard_shortcut", { slug });
    setShowJobBuilder(true);
  });

  return (
    <Button
      text="Create Job"
      shortcut="J"
      disabledTooltip={
        exceededJobs ? (
          <TooltipContent
            title="Your workspace has exceeded its monthly jobs limit. We're still collecting data on your existing jobs, but you need to Upgrade to add more jobs."
            cta={`Upgrade to ${nextPlan.name}`}
            href={`/${slug}/upgrade`}
          />
        ) : undefined
      }
      onClick={() => {
        posthog.capture("job_builder_button_click", {
          slug,
          exceededJobs,
          source: "create_job_button",
          timestamp: new Date().toISOString(),
        });
        setShowJobBuilder(true);
      }}
    />
  );
}

export function useJobBuilder({
  props,
  duplicateProps,
}: {
  props?: ExpandedJobProps;
  duplicateProps?: ExpandedJobProps;
} = {}) {
  const [showJobBuilder, setShowJobBuilder] = useState(false);

  const JobBuilderCallback = useCallback(() => {
    return (
      <JobBuilder
        showJobBuilder={showJobBuilder}
        setShowJobBuilder={setShowJobBuilder}
        props={props}
        duplicateProps={duplicateProps}
      />
    );
  }, [showJobBuilder, props, duplicateProps]);

  const CreateJobButtonCallback = useCallback(() => {
    return <CreateJobButton setShowJobBuilder={setShowJobBuilder} />;
  }, []);

  return useMemo(
    () => ({
      showJobBuilder,
      setShowJobBuilder,
      JobBuilder: JobBuilderCallback,
      CreateJobButton: CreateJobButtonCallback,
    }),
    [showJobBuilder, JobBuilderCallback, CreateJobButtonCallback]
  );
}
